// DFM WebUSB — adb прямо в браузере. Порт логики dfm.py на JavaScript.
// Собирается командой `npm run build` в web/dist/webusb.js (глобальный объект DFMWebUSB).

import { Adb, AdbDaemonTransport } from "@yume-chan/adb";
import { AdbDaemonWebUsbDevice, AdbDaemonWebUsbDeviceManager } from "@yume-chan/adb-daemon-webusb";
import AdbWebCredentialStore from "@yume-chan/adb-credential-web";
import { apkInfo } from "./apk.js";

export { apkInfo };

const Manager = AdbDaemonWebUsbDeviceManager.BROWSER;
const credentialStore = new AdbWebCredentialStore("DFM Web");
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const TEMP_APK = "/data/local/tmp/dfm_install_app.apk";
const DOWNLOAD_DIR = "/sdcard/Download/DFM_APK";
const PRIV_APP_DIR = "/system/priv-app";
const SAFE_FOLDER = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;
const KNOWN_LAUNCH_ACTIVITIES = { "com.schiztech.swapps": "com.schiztech.swapps/.SettingsActivity" };

export function supportStatus() {
  if (!window.isSecureContext) return { ok: false, reason: "insecure" };
  if (!("usb" in navigator) || !Manager) return { ok: false, reason: "browser" };
  return { ok: true };
}

const q = (v) => "'" + String(v).replace(/'/g, "'\\''") + "'"; // shlex.quote

/* ======================================================================= */
/*  USB-сессия: подключение, авторизация, переподключение                   */
/* ======================================================================= */

export class UsbSession extends EventTarget {
  adb = null;
  device = null;
  serial = null;
  state = "idle"; // idle | connecting | auth | ready | reconnecting
  #cache = null;

  #set(state, detail = {}) {
    this.state = state;
    this.dispatchEvent(new CustomEvent("state", { detail: { state, ...detail } }));
  }

  /** Окно браузера «выберите устройство». Нужен клик пользователя. */
  async request() {
    const device = await Manager.requestDevice();
    if (!device) return false;
    await this.open(device);
    return true;
  }

  /** Подключиться к уже разрешённому ранее устройству без окна выбора. */
  async autoConnect() {
    if (this.adb) return true;
    const list = await Manager.getDevices();
    if (!list.length) return false;
    await this.open(list[0]);
    return true;
  }

  async open(device) {
    this.#set("connecting", { name: device.name });
    let connection;
    try {
      connection = await device.connect();
    } catch (e) {
      this.#set("idle");
      if (e instanceof AdbDaemonWebUsbDevice.DeviceBusyError) {
        const err = new Error("USB занят другой программой (adb.exe, Android Studio). Выполните: adb kill-server");
        err.code = "BUSY";
        throw err;
      }
      throw e;
    }
    this.#set("auth", { name: device.name });
    const transport = await AdbDaemonTransport.authenticate({ serial: device.serial, connection, credentialStore });
    this.adb = new Adb(transport);
    this.device = device;
    this.serial = device.serial;
    this.#cache = null;
    this.#set("ready");
    transport.disconnected.then(() => {
      if (this.adb?.transport !== transport) return;
      this.adb = null;
      if (this.state !== "reconnecting") this.#set("idle", { lost: true });
    });
  }

  async disconnect() {
    const adb = this.adb;
    this.adb = null;
    this.serial = null;
    this.#set("idle");
    await adb?.close().catch(() => {});
  }

  /** После adb root / reboot устройство переподключается по USB — ждём и цепляемся снова. */
  async reconnect(timeoutMs = 120000) {
    const serial = this.serial;
    this.#set("reconnecting");
    await this.adb?.close().catch(() => {});
    this.adb = null;
    const deadline = Date.now() + timeoutMs;
    await sleep(2000);
    while (Date.now() < deadline) {
      const list = await Manager.getDevices();
      const dev = list.find((d) => d.serial === serial);
      if (dev) {
        try {
          await this.open(dev);
          return true;
        } catch {
          /* устройство ещё поднимается */
        }
      }
      await sleep(1500);
    }
    this.#set("idle", { lost: true });
    return false;
  }

  /* ---------------- низкоуровневые команды ---------------- */

  #need() {
    if (!this.adb) throw new Error("Устройство не подключено");
    return this.adb;
  }

  /** shell-команда → { stdout, stderr, code } */
  async shell(cmd) {
    const adb = this.#need();
    const sp = adb.subprocess.shellProtocol;
    if (sp?.isSupported) {
      const r = await sp.spawnWaitText(cmd);
      return { stdout: r.stdout, stderr: r.stderr, code: r.exitCode };
    }
    const out = await adb.subprocess.noneProtocol.spawnWaitText(`${cmd}; echo __DFM_RC=$?`);
    const m = out.match(/__DFM_RC=(\d+)\s*$/);
    return { stdout: out.replace(/__DFM_RC=\d+\s*$/, ""), stderr: "", code: m ? Number(m[1]) : 0 };
  }

  /** Бинарный вывод команды (screencap). */
  async execBytes(cmd) {
    const adb = this.#need();
    return adb.subprocess.noneProtocol.spawnWait(cmd);
  }

  async service(name) {
    return this.#need().createSocketAndWait(name);
  }

  /** adb push с прогрессом */
  async push(blob, remote, onProgress, permission = 0o644) {
    const adb = this.#need();
    const sync = await adb.sync();
    try {
      await sync.write({
        filename: remote,
        file: progressStream(blob, onProgress),
        permission,
        mtime: (Date.now() / 1000) | 0,
      });
    } finally {
      await sync.dispose();
    }
  }

  /** Аналог `adb install`: стриминговая установка через `cmd package install -S`. */
  async streamInstall(blob, flags, onProgress) {
    const adb = this.#need();
    const proc = await adb.subprocess.noneProtocol.spawn(["cmd", "package", "install", "-S", String(blob.size), ...flags]);
    const output = readText(proc.output);
    await progressStream(blob, onProgress).pipeTo(proc.stdin).catch(() => {});
    const text = await output;
    return { stdout: text.trim(), stderr: "", code: /success/i.test(text) ? 0 : 1 };
  }

  /* ---------------- информация об устройстве ---------------- */

  async info(force = false) {
    if (this.#cache && !force && Date.now() - this.#cache.ts < 20000) return this.#cache.data;
    const props = {};
    const pr = await this.shell("getprop");
    for (const m of pr.stdout.matchAll(/\[([^\]]+)\]: \[([^\]]*)\]/g)) props[m[1]] = m[2];
    const bat = (await this.shell("dumpsys battery")).stdout;
    const level = bat.match(/level:\s*(\d+)/);
    const status = bat.match(/status:\s*(\d+)/);
    let storage = null;
    const df = (await this.shell("df -k /data")).stdout.trim().split("\n").filter(Boolean);
    if (df.length >= 2) {
      const parts = df[df.length - 1].split(/\s+/);
      const total = Number(parts[1]), used = Number(parts[2]);
      if (total && !Number.isNaN(used)) storage = { total: total * 1024, used: used * 1024 };
    }
    const data = {
      serial: this.serial,
      state: "device",
      transport: "webusb",
      manufacturer: props["ro.product.manufacturer"] || "",
      brand: props["ro.product.brand"] || "",
      model: props["ro.product.model"] || this.device?.name || this.serial,
      android: props["ro.build.version.release"] || "",
      sdk: props["ro.build.version.sdk"] || "",
      abi: props["ro.product.cpu.abi"] || "",
      battery: { level: level ? Number(level[1]) : null, charging: !!status && ["2", "5"].includes(status[1]) },
      storage,
    };
    this.#cache = { ts: Date.now(), data };
    return data;
  }

  async screenshot() {
    const png = await this.execBytes("screencap -p");
    if (!png || png[0] !== 0x89) throw new Error("screencap не вернул PNG");
    return new Blob([png], { type: "image/png" });
  }
}

function progressStream(blob, onProgress) {
  let sent = 0;
  const total = blob.size;
  return blob.stream().pipeThrough(
    new TransformStream({
      transform(chunk, ctl) {
        sent += chunk.byteLength;
        onProgress?.(sent, total);
        ctl.enqueue(chunk);
      },
    }),
  );
}

async function readText(stream) {
  const reader = stream.getReader();
  const dec = new TextDecoder();
  let text = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    text += dec.decode(value, { stream: true });
  }
  return text + dec.decode();
}

/* ======================================================================= */
/*  Задача: поток событий для интерфейса (как SSE у локального агента)      */
/* ======================================================================= */

export class Job {
  events = [];
  #subs = new Set();
  status = "running";
  steps = 0;
  report = [];
  constructor(title, totalSteps = 0) {
    this.title = title;
    this.total = totalSteps;
    this.t0 = performance.now();
  }
  emit(type, data = {}) {
    const ev = { type, t: (performance.now() - this.t0) / 1000, ...data };
    this.events.push(ev);
    this.#subs.forEach((cb) => cb(ev));
  }
  subscribe(cb) {
    this.events.forEach(cb);
    this.#subs.add(cb);
    return () => this.#subs.delete(cb);
  }
}

/* ======================================================================= */
/*  Движок: методы установки из dfm.py                                       */
/* ======================================================================= */

export const INSTALL_MENU = [
  ["1", "adb install -r"],
  ["2", "adb install -r -d"],
  ["3", "adb install -r -g"],
  ["4", "adb install --user 0 -r"],
  ["5", "adb install --no-streaming -r"],
  ["6", "adb install --no-incremental -r"],
  ["7", "push + pm install -r"],
  ["8", "push + pm install -r -d"],
  ["9", "push + pm install -r -g"],
  ["10", "push + pm install --user 0 -r"],
  ["11", "push + session install-create"],
  ["12", "push + session install-create -r"],
  ["13", "push + session install-create -r -d"],
  ["14", "push + session install-create -r -g"],
  ["15", "push + session install-create --user 0 -r"],
  ["16", "new user + adb install --user <id> -r"],
  ["17", "new user + adb install --user <id> -r -d"],
  ["18", "new user + push + pm install --user <id> -r"],
  ["19", "new user + push + pm install --user <id> -r -g"],
  ["20", "new user + push + session install-create --user <id>"],
  ["21", "new user + push + session install-create --user <id> -r"],
  ["22", "new user + push + session install-create --user <id> -r -g"],
  ["23", "system app: root + remount + push to /system/priv-app"],
  ["24", "copy APK to Download, open file manager, then installer"],
  ["28", "post-install grant permissions for known APKs"],
];

export const TOOLS = [
  ["diagnose", "Проверка устройства"],
  ["system", "Топ-диагностика системы"],
  ["installers", "Включить установщики и файловые менеджеры"],
  ["unknown", "Открыть настройки неизвестных источников"],
];

const AUTO_MODE_STEPS = 6 + 4 + 5 + 7 + 1;

const output = (r) => [r.stdout, r.stderr].map((s) => (s || "").trim()).filter(Boolean).join("\n").trim();

function isSuccess(r) {
  const o = output(r).toLowerCase();
  return r.code === 0 && (o.includes("success") || o === "");
}
function shouldStop(r) {
  const o = output(r).toLowerCase();
  return ["device policy", "securityexception", "user restricted", "install not allowed", "permission denied", "not allowed to install"].some((b) => o.includes(b));
}
function intentStarted(r) {
  const o = output(r).toLowerCase();
  return r.code === 0 && !["unable to resolve intent", "activity not started", "error:", "exception", "not found", "permission denied"].some((m) => o.includes(m));
}

export function systemFolderName(name) {
  const stem = name.replace(/\.apk$/i, "").split("_")[0];
  return stem.replace(/[^A-Za-z0-9.-]/g, "").replace(/^[.-]+|[.-]+$/g, "") || "DFMApp";
}

/* реестр системных приложений — в localStorage браузера */
const SYS_KEY = "dfm.systemApps";
export function loadSystemApps() {
  try { return JSON.parse(localStorage.getItem(SYS_KEY)) || {}; } catch { return {}; }
}
function saveSystemApps(apps) {
  localStorage.setItem(SYS_KEY, JSON.stringify(apps));
}

class Runner {
  constructor(session, job, options = {}) {
    this.s = session;
    this.job = job;
    this.o = options;
  }

  /* ---- вывод (как print / print_header / print_result) ---- */
  log(text) {
    for (const line of String(text).split("\n")) {
      if (!line.trim()) continue;
      this.job.report.push(line);
      this.job.emit("log", { text: line });
    }
  }
  header(title) {
    this.job.report.push("", "=".repeat(60), title, "=".repeat(60));
    this.job.emit("header", { title });
  }
  step(title) {
    this.job.steps += 1;
    this.job.report.push("", "Пробуем: " + title);
    this.job.emit("step", { title, n: this.job.steps, total: this.job.total });
  }
  result(title, r) {
    this.header(title);
    const o = output(r);
    this.log(o || "(no output)");
    this.job.report.push("Exit code: " + r.code);
    this.job.emit("exit", { code: String(r.code) });
  }
  ask(question) {
    const ql = question.toLowerCase();
    let answer = false;
    if (ql.startsWith("удалить")) answer = true;
    else if (ql.includes("перезагруз")) answer = !!this.o.reboot;
    else if (ql.includes("системн")) answer = !!this.o.system;
    else if (ql.includes("пользовател")) answer = !!this.o.newUser;
    this.log(`? ${question} -> ${answer ? "да" : "нет"} (настройка на сайте)`);
    return answer;
  }
  progress(label) {
    let last = -1;
    return (sent, total) => {
      const pct = Math.floor((sent / total) * 100);
      if (pct !== last && (pct % 10 === 0 || pct === 100)) {
        last = pct;
        this.job.emit("transfer", { label, pct, sent, total });
      }
    };
  }

  /* ---- обёртки команд ---- */
  async sh(cmd) {
    try { return await this.s.shell(cmd); }
    catch (e) { return { stdout: "", stderr: String(e.message || e), code: -1 }; }
  }
  async shR(title, cmd) {
    const r = await this.sh(cmd);
    this.result(title, r);
    return r;
  }

  async pushTemp(file) {
    this.header("Копируем APK во временную папку телефона");
    await this.sh(`rm -f ${TEMP_APK}`);
    try {
      await this.s.push(file, TEMP_APK, this.progress("push"));
      this.result(`adb push -> ${TEMP_APK}`, { stdout: `${file.name}: 1 file pushed`, stderr: "", code: 0 });
      return true;
    } catch (e) {
      this.result(`adb push -> ${TEMP_APK}`, { stdout: "", stderr: String(e.message || e), code: 1 });
      return false;
    }
  }
  cleanupTemp() { return this.sh(`rm -f ${TEMP_APK}`); }

  async adbInstall(file, title, flags) {
    this.step(title);
    let r;
    try {
      r = await this.s.streamInstall(file, flags, this.progress("install"));
      if (/can't find service|unknown command|not found/i.test(r.stdout)) throw new Error(r.stdout);
    } catch (e) {
      this.log("Стриминговая установка недоступна, ставим через push + pm install: " + (e.message || e));
      if (!(await this.pushTemp(file))) return { stdout: "", stderr: "push failed", code: 1 };
      r = await this.sh(`pm install ${flags.join(" ")} ${TEMP_APK}`);
      await this.cleanupTemp();
    }
    this.result(title, r);
    return r;
  }

  async pmInstall(title, flags) {
    this.step(title);
    return this.shR(title, `pm install ${flags.join(" ")} ${TEMP_APK}`);
  }

  async sessionInstall(remote, options) {
    const opt = options.join(" ");
    const create = await this.shR(`Session install-create ${opt}`.trim(), `pm install-create ${opt}`.trim());
    const id = (output(create).match(/\d+/) || [])[0];
    if (!id) return create;
    const size = (await this.sh(`stat -c %s ${q(remote)}`)).stdout.trim();
    const sizeArg = /^\d+$/.test(size) ? `-S ${size}` : "";
    const write = await this.shR(`Session install-write #${id}`, `pm install-write ${sizeArg} ${id} base.apk ${q(remote)}`);
    if (write.code !== 0) { await this.sh(`pm install-abandon ${id}`); return write; }
    return this.shR(`Session install-commit #${id}`, `pm install-commit ${id}`);
  }

  /* ---- новый пользователь ---- */
  async createUser(confirmed = false) {
    this.header("Создание нового пользователя Android");
    if (!confirmed && !this.ask("Создать нового пользователя Android?")) { this.log("Создание пользователя отменено."); return null; }
    const r = await this.shR("pm create-user DFM Installer", `pm create-user ${q("DFM Installer")}`);
    const id = (output(r).match(/\d+/) || [])[0];
    if (!id) { this.log("Не удалось создать пользователя или получить его ID."); return null; }
    await this.shR("pm list users", "pm list users");
    return id;
  }
  async switchUser(id) {
    this.log(`Пробуем переключиться на пользователя ${id}...`);
    await this.shR(`am switch-user ${id}`, `am switch-user ${id}`);
  }

  /* ---- диагностика и инструменты ---- */
  async diagnose() {
    this.header("Проверка устройства");
    for (const [t, c] of [
      ["Пользователи", "pm list users"], ["Device Owner", "dpm get-device-owner"], ["Profile Owner", "dpm get-profile-owner"],
      ["Модель", "getprop ro.product.model"], ["Производитель", "getprop ro.product.manufacturer"], ["Android SDK", "getprop ro.build.version.sdk"],
    ]) await this.shR(t, c);
    return true;
  }
  async systemDiagnostics() {
    this.header("Топ-диагностика системы");
    for (const [t, c] of [
      ["Пользователи", "pm list users"], ["cmd user list", "cmd user list"], ["Device Owner", "dpm get-device-owner"],
      ["Profile Owner", "dpm get-profile-owner"], ["User restrictions", "pm get-user-restrictions"], ["Disabled packages", "pm list packages -d"],
      ["Activity для APK install", "cmd package query-intent-activities -a android.intent.action.VIEW -t application/vnd.android.package-archive"],
      ["Activity для папок", "cmd package query-intent-activities -a android.intent.action.VIEW -t resource/folder"],
      ["Activity для unknown sources settings", "cmd package query-intent-activities -a android.settings.MANAGE_UNKNOWN_APP_SOURCES"],
      ["Пакеты установщиков и файловых менеджеров", "pm list packages | grep -Ei 'file|files|documents|explorer|manager|installer|permission|settings'"],
    ]) await this.shR(t, c);
    return true;
  }
  async enableInstallers() {
    this.header("Включаем системные установщики и файловые менеджеры");
    for (const p of ["com.android.packageinstaller", "com.google.android.packageinstaller", "com.android.permissioncontroller", "com.android.documentsui",
      "com.google.android.documentsui", "com.google.android.apps.nbu.files", "com.sec.android.app.myfiles", "com.mi.android.globalFileexplorer",
      "com.huawei.hidisk", "com.coloros.filemanager", "com.transsion.filemanager"]) {
      await this.shR(`pm enable --user 0 ${p}`, `pm enable --user 0 ${q(p)}`);
      await this.shR(`install-existing --user 0 ${p}`, `cmd package install-existing --user 0 ${q(p)}`);
    }
    return true;
  }
  async openUnknownSources() {
    this.header("Открываем настройки установки из неизвестных источников");
    for (const [t, c] of [
      ["MANAGE_UNKNOWN_APP_SOURCES", "am start -a android.settings.MANAGE_UNKNOWN_APP_SOURCES"],
      ["SECURITY_SETTINGS", "am start -a android.settings.SECURITY_SETTINGS"],
      ["APPLICATION_SETTINGS", "am start -a android.settings.APPLICATION_SETTINGS"],
      ["Settings app", "monkey -p com.android.settings 1"],
    ]) if (intentStarted(await this.shR(t, c))) return true;
    return false;
  }
  async grantPostInstall(file) {
    this.header("Post-install permissions");
    if (!/morelocale/i.test(file.name)) { this.log("Для этого APK нет известных специальных permission-команд."); return false; }
    const pkg = "jp.co.c_lis.ccl.morelocale";
    const a = await this.shR(`Grant CHANGE_CONFIGURATION to ${pkg}`, `pm grant ${pkg} android.permission.CHANGE_CONFIGURATION`);
    const b = await this.shR(`Check package ${pkg}`, `pm path ${pkg}`);
    return a.code === 0 || b.code === 0;
  }

  /* ---- метод 24: Download + файловый менеджер + автонажатие ---- */
  async copyToDownload(file) {
    const safe = file.name.replace(/["']/g, "");
    const remote = `${DOWNLOAD_DIR}/${safe.toLowerCase().endsWith(".apk") ? safe : safe + ".apk"}`;
    this.header("Копируем APK в Download телефона");
    await this.sh(`mkdir -p ${q(DOWNLOAD_DIR)}`);
    try {
      await this.s.push(file, remote, this.progress("push"));
      this.result(`adb push -> ${remote}`, { stdout: "1 file pushed", stderr: "", code: 0 });
    } catch (e) {
      this.result(`adb push -> ${remote}`, { stdout: "", stderr: String(e.message || e), code: 1 });
      return null;
    }
    await this.sh(`am broadcast -a android.intent.action.MEDIA_SCANNER_SCAN_FILE -d ${q("file://" + remote)}`);
    this.log(`APK сохранен здесь: ${remote}`);
    return remote;
  }
  contentUri(remote) {
    const doc = remote.startsWith("/sdcard/") ? remote.slice(8) : remote.replace(/^\/+/, "");
    return "content://com.android.externalstorage.documents/document/primary%3A" + encodeURIComponent(doc);
  }
  async openFileManager(remote) {
    const cmds = [
      ["Открыть папку DFM_APK в файловом менеджере", `am start -a android.intent.action.VIEW -d ${q("file://" + DOWNLOAD_DIR)} -t resource/folder`],
      ["Открыть DocumentsUI через content URI", `am start -a android.intent.action.VIEW -d ${q("content://com.android.externalstorage.documents/root/primary")}`],
      ["Открыть APK через content://", `am start --grant-read-uri-permission -a android.intent.action.VIEW -d ${q(this.contentUri(remote))} -t application/vnd.android.package-archive`],
    ];
    for (const [t, c] of cmds) await this.shR(t, c);
  }
  async openInstaller(remote) {
    const uris = [["content://", this.contentUri(remote)], ["file://", "file://" + remote]];
    const comps = [null, "com.android.packageinstaller/.InstallStart", "com.android.packageinstaller/.PackageInstallerActivity",
      "com.google.android.packageinstaller/com.android.packageinstaller.InstallStart",
      "com.google.android.packageinstaller/com.android.packageinstaller.PackageInstallerActivity"];
    this.header("Открываем APK: сначала content://, потом file://");
    for (const comp of comps) for (const [kind, uri] of uris) {
      const t = `Открыть ${comp || "системный установщик APK"} через ${kind}`;
      const r = await this.shR(t, `am start --grant-read-uri-permission ${comp ? "-n " + comp + " " : ""}-a android.intent.action.VIEW -t application/vnd.android.package-archive -d ${q(uri)}`);
      if (intentStarted(r)) { this.log(`Успешно открыт вариант: ${t}`); return; }
      this.log("Не открылось, пробуем следующий вариант...");
    }
    for (const p of ["com.android.packageinstaller", "com.google.android.packageinstaller", "com.android.permissioncontroller"])
      await this.shR(`Открыть ${p}`, `monkey -p ${p} 1`);
  }
  async tapLabels(labels, attempts, delay = 1000) {
    for (let i = 1; i <= attempts; i++) {
      await sleep(delay);
      const dump = await this.sh("uiautomator dump /sdcard/dfm_window.xml >/dev/null && cat /sdcard/dfm_window.xml");
      const xml = dump.stdout;
      if (!xml) continue;
      for (const node of xml.match(/<node\b[^>]*>/g) || []) {
        const low = node.toLowerCase();
        if (!labels.some((l) => low.includes(l))) continue;
        const m = node.match(/bounds="\[(\d+),(\d+)\]\[(\d+),(\d+)\]"/);
        if (!m) continue;
        const x = ((+m[1] + +m[3]) / 2) | 0, y = ((+m[2] + +m[4]) / 2) | 0;
        const tap = await this.shR(`Нажать кнопку UI (${x}, ${y})`, `input tap ${x} ${y}`);
        return tap.code === 0;
      }
      this.log(`Попытка ${i}: нужная кнопка пока не найдена.`);
    }
    return false;
  }
  async autoConfirm() {
    this.header("Автонажатие кнопки установки");
    if (await this.tapLabels(["install", "установить", "ўрнатиш", "o'rnatish", "орнату", "instalar", "installer", "continue", "продолжить", "далее"], 10)) {
      this.log("Кнопка установки нажата. Ждем завершения...");
      await this.tapLabels(["done", "готово", "open", "открыть", "ok", "понятно"], 8);
      return true;
    }
    this.log("Не удалось найти кнопку установки. Нажми APK вручную или выбери другой метод.");
    return false;
  }
  async viaDownload(file) {
    const remote = await this.copyToDownload(file);
    if (!remote) return false;
    await this.openFileManager(remote);
    await this.openInstaller(remote);
    await this.autoConfirm();
    return true;
  }

  /* ---- метод 23: системная установка ---- */
  async rootRemount() {
    for (const attempt of [1, 2]) {
      let rootOut;
      try { rootOut = await this.s.service("root:"); } catch (e) { rootOut = String(e.message || e); }
      this.result("adb root", { stdout: rootOut, stderr: "", code: /cannot|error/i.test(rootOut) ? 1 : 0 });
      if (/cannot run as root|production builds/i.test(rootOut)) {
        this.log("adbd не может работать как root на этой прошивке — системная установка невозможна.");
        return false;
      }
      if (/restarting adbd as root/i.test(rootOut)) {
        this.log("adbd перезапускается как root — переподключаемся по USB...");
        if (!(await this.s.reconnect(60000))) { this.log("Устройство не вернулось после adb root."); return false; }
      }
      let out;
      try { out = await this.s.service("remount:"); } catch (e) { out = String(e.message || e); }
      const low = out.toLowerCase();
      const ok = low.includes("remount succeeded") || (!low.includes("fail") && !low.includes("not") && !low.includes("reboot"));
      this.result("adb remount", { stdout: out, stderr: "", code: ok ? 0 : 1 });
      if (low.includes("reboot") && (low.includes("verity") || low.includes("now reboot") || low.includes("take effect"))) {
        this.log("Для remount нужна перезагрузка (отключена проверка verity).");
        if (attempt === 1 && this.ask("Перезагрузить устройство, чтобы remount заработал?")) {
          if (!(await this.reboot())) return false;
          continue;
        }
        return false;
      }
      if (ok) return true;
      this.log("adb remount не удался.");
      return false;
    }
    return false;
  }
  async reboot() {
    this.header("Перезагрузка устройства");
    try { await this.s.adb.power.reboot(); } catch { /* соединение рвётся — это нормально */ }
    this.log("Ждем загрузки Android...");
    await sleep(8000);
    if (!(await this.s.reconnect(240000))) { this.log("Устройство не вернулось после перезагрузки."); return false; }
    for (let i = 0; i < 60; i++) {
      if ((await this.sh("getprop sys.boot_completed")).stdout.trim() === "1") { await sleep(3000); this.log("Android загрузился."); return true; }
      await sleep(2000);
    }
    this.log("Устройство не загрузилось за отведенное время.");
    return false;
  }
  async launch(pkg) {
    const comp = KNOWN_LAUNCH_ACTIVITIES[pkg];
    if (comp && intentStarted(await this.shR(`am start -n ${comp}`, `am start -n ${q(comp)}`))) return true;
    return (await this.shR(`Запуск ${pkg}`, `monkey -p ${q(pkg)} -c android.intent.category.LAUNCHER 1`)).code === 0;
  }
  async installed(pkg) {
    if (!pkg) return false;
    const r = await this.sh(`pm path ${q(pkg)}`);
    return r.code === 0 && r.stdout.includes("package:");
  }
  async installSystem(file, pkg) {
    const folder = systemFolderName(file.name);
    const dir = `${PRIV_APP_DIR}/${folder}`;
    const remote = `${dir}/${folder}.apk`;
    this.header("Системная установка: /system/priv-app (root + remount)");
    this.step(`system priv-app -> ${remote}`);
    this.log(`Пакет: ${pkg || "не удалось определить"}`);
    if (!(await this.rootRemount())) return false;
    if ((await this.shR(`mkdir -p ${dir}`, `mkdir -p ${q(dir)}`)).code !== 0) return false;
    try {
      await this.s.push(file, remote, this.progress("push"), 0o644);
      this.result(`adb push -> ${remote}`, { stdout: "1 file pushed", stderr: "", code: 0 });
    } catch (e) {
      this.result(`adb push -> ${remote}`, { stdout: "", stderr: String(e.message || e), code: 1 });
      this.log("Не удалось записать APK в /system.");
      return false;
    }
    await this.shR(`chmod 755 ${dir}`, `chmod 755 ${q(dir)}`);
    if ((await this.shR(`chmod 644 ${remote}`, `chmod 644 ${q(remote)}`)).code !== 0) return false;
    await this.shR(`ls -lh ${dir}`, `ls -lh ${q(dir)}`);

    const apps = loadSystemApps();
    apps[folder] = { folder, package: pkg, apk: file.name, remote, serial: this.s.serial, installed: new Date().toISOString().slice(0, 19) };
    saveSystemApps(apps);

    if (await this.installed(pkg)) await this.launch(pkg);
    if (this.ask("Перезагрузить устройство сейчас, чтобы Android подхватил системное приложение?")) {
      if ((await this.reboot()) && pkg) {
        if (await this.installed(pkg)) { this.log(`Системное приложение ${pkg} зарегистрировано.`); await this.launch(pkg); }
        else { this.log(`После перезагрузки ${pkg} не найден в pm.`); return false; }
      }
    } else this.log("APK лежит в /system/priv-app. Перезагрузи устройство, чтобы приложение появилось.");
    this.log(`Готово: APK установлен как системный (${remote}).`);
    return true;
  }
  async removeSystem(folder, pkg) {
    if (!SAFE_FOLDER.test(folder || "")) { this.log(`Недопустимое имя папки: ${folder}`); return false; }
    const dir = `${PRIV_APP_DIR}/${folder}`;
    pkg = pkg || loadSystemApps()[folder]?.package;
    this.header(`Удаление системного приложения: ${dir}`);
    if (!(await this.rootRemount())) return false;
    if (pkg) await this.shR(`pm uninstall ${pkg} (обновления/данные)`, `pm uninstall ${q(pkg)}`);
    await this.shR(`rm -rf ${dir}`, `rm -rf ${q(dir)}`);
    const check = await this.shR(`Проверка: ls ${dir}`, `ls ${q(dir)}`);
    if (check.code === 0) { this.log("Папка все еще существует — удалить не получилось."); return false; }
    const apps = loadSystemApps();
    delete apps[folder];
    saveSystemApps(apps);
    if (this.ask("Перезагрузить устройство сейчас, чтобы Android забыл приложение?")) await this.reboot();
    else this.log("Перезагрузи устройство, чтобы изменения вступили в силу.");
    this.log(`Готово: ${dir} удалена.`);
    return true;
  }

  /* ---- выбранный метод ---- */
  async selected(file, pkg, m) {
    const direct = { 1: ["-r"], 2: ["-r", "-d"], 3: ["-r", "-g"], 4: ["--user", "0", "-r"], 6: ["-r"] };
    const titles = Object.fromEntries(INSTALL_MENU);
    if (direct[m]) return isSuccess(await this.adbInstall(file, titles[m], direct[m]));
    if (m === "5") {
      this.step(titles[m]);
      if (!(await this.pushTemp(file))) return false;
      try { return isSuccess(await this.shR(titles[m], `pm install -r ${TEMP_APK}`)); } finally { await this.cleanupTemp(); }
    }
    const pm = { 7: ["-r"], 8: ["-r", "-d"], 9: ["-r", "-g"], 10: ["--user", "0", "-r"] };
    const ses = { 11: [], 12: ["-r"], 13: ["-r", "-d"], 14: ["-r", "-g"], 15: ["--user", "0", "-r"] };
    if (pm[m] || ses[m]) {
      if (!(await this.pushTemp(file))) return false;
      try {
        if (pm[m]) return isSuccess(await this.pmInstall(`pm install ${pm[m].join(" ")}`, pm[m]));
        this.step("Session API: pm install-create " + ses[m].join(" "));
        return isSuccess(await this.sessionInstall(TEMP_APK, ses[m]));
      } finally { await this.cleanupTemp(); }
    }
    if (+m >= 16 && +m <= 22) {
      const id = await this.createUser();
      if (!id) return false;
      let ok = false;
      if (m === "16" || m === "17") {
        ok = isSuccess(await this.adbInstall(file, `adb install --user ${id} -r${m === "17" ? " -d" : ""}`, ["--user", id, "-r", ...(m === "17" ? ["-d"] : [])]));
      } else {
        if (!(await this.pushTemp(file))) return false;
        try {
          if (m === "18") ok = isSuccess(await this.pmInstall(`pm install --user ${id} -r`, ["--user", id, "-r"]));
          else if (m === "19") ok = isSuccess(await this.pmInstall(`pm install --user ${id} -r -g`, ["--user", id, "-r", "-g"]));
          else {
            const opts = { 20: ["--user", id], 21: ["--user", id, "-r"], 22: ["--user", id, "-r", "-g"] }[m];
            this.step("Session API для нового пользователя: pm install-create " + opts.join(" "));
            ok = isSuccess(await this.sessionInstall(TEMP_APK, opts));
          }
        } finally { await this.cleanupTemp(); }
      }
      if (ok) await this.switchUser(id);
      return ok;
    }
    if (m === "23") return this.installSystem(file, pkg);
    if (m === "24") return this.viaDownload(file);
    if (m === "28") return this.grantPostInstall(file);
    return false;
  }

  /* ---- Авто: все методы по очереди ---- */
  async auto(file, pkg) {
    const results = [];
    for (const [t, f] of [["adb install -r", ["-r"]], ["adb install -r -d", ["-r", "-d"]], ["adb install -r -g", ["-r", "-g"]],
      ["adb install --user 0 -r", ["--user", "0", "-r"]], ["adb install --no-streaming -r", ["-r"]], ["adb install --no-incremental -r", ["-r"]]]) {
      const r = await this.adbInstall(file, t, f);
      results.push(r);
      if (isSuccess(r)) return true;
    }
    const temp = await this.pushTemp(file);
    if (temp) {
      for (const f of [["-r"], ["-r", "-d"], ["-r", "-g"], ["--user", "0", "-r"]]) {
        const r = await this.pmInstall(`pm install ${f.join(" ")}`, f);
        results.push(r);
        if (isSuccess(r)) { await this.cleanupTemp(); return true; }
      }
      for (const o of [[], ["-r"], ["-r", "-d"], ["-r", "-g"], ["--user", "0", "-r"]]) {
        this.step("Session API: pm install-create " + o.join(" "));
        const r = await this.sessionInstall(TEMP_APK, o);
        results.push(r);
        if (isSuccess(r)) { await this.cleanupTemp(); return true; }
      }
    }
    this.header("Fallback: новый пользователь Android");
    if (this.ask("Создать нового пользователя Android и попробовать установить APK туда?")) {
      const id = await this.createUser(true);
      if (id) {
        const tries = [
          () => this.adbInstall(file, `adb install --user ${id} -r`, ["--user", id, "-r"]),
          () => this.adbInstall(file, `adb install --user ${id} -r -d`, ["--user", id, "-r", "-d"]),
        ];
        if (temp || (await this.pushTemp(file))) {
          tries.push(
            () => this.pmInstall(`pm install --user ${id} -r`, ["--user", id, "-r"]),
            () => this.pmInstall(`pm install --user ${id} -r -g`, ["--user", id, "-r", "-g"]),
            ...[["--user", id], ["--user", id, "-r"], ["--user", id, "-r", "-g"]].map((o) => () => {
              this.step("Session API для нового пользователя: pm install-create " + o.join(" "));
              return this.sessionInstall(TEMP_APK, o);
            }),
          );
        }
        for (const t of tries) {
          if (isSuccess(await t())) { await this.switchUser(id); await this.cleanupTemp(); return true; }
        }
        this.log(`Новый пользователь создан с ID ${id}, но APK туда не установился. Удалить: pm remove-user ${id}`);
      }
    } else this.log("Пропускаем установку через нового пользователя.");

    if (this.ask("Попробовать системную установку в /system/priv-app (adb root + remount)?")) {
      if (await this.installSystem(file, pkg)) { await this.cleanupTemp(); return true; }
    } else this.log("Пропускаем системную установку.");

    await this.viaDownload(file);
    await this.cleanupTemp();
    this.header("Итог");
    this.log("Автоматически установить APK не получилось.");
    if (results.some(shouldStop)) this.log("Похоже, устройство блокирует установку политикой безопасности (Device Owner / ограничения).");
    this.log("Вручную: Files → Download → DFM_APK → нажми APK и разреши установку из этого источника.");
    return false;
  }
}

const TOOL_FUNCS = {
  diagnose: (r) => r.diagnose(),
  system: (r) => r.systemDiagnostics(),
  installers: (r) => r.enableInstallers(),
  unknown: (r) => r.openUnknownSources(),
};

function runJob(session, job, options, fn) {
  const runner = new Runner(session, job, options);
  job.emit("start", { title: job.title, serial: session.serial });
  (async () => {
    let ok = false;
    try {
      ok = !!(await fn(runner));
      job.status = ok ? "success" : "done";
    } catch (e) {
      job.status = "error";
      runner.log("Ошибка: " + (e?.message || e));
      console.error(e);
    }
    const report = ["DFM APK Installer (WebUSB)", `Report time: ${new Date().toISOString().slice(0, 19)}`, "", ...job.report].join("\n");
    try { localStorage.setItem("dfm.lastReport", report); } catch { /* квота */ }
    job.emit("end", { status: job.status, ok });
  })();
  return job;
}

export function startInstall(session, file, pkg, method, options) {
  const titles = Object.fromEntries(INSTALL_MENU);
  const auto = method === "0";
  const job = new Job(`${file.name} — ${auto ? "Авто: все методы по очереди" : titles[method] || method}`, auto ? AUTO_MODE_STEPS : 1);
  return runJob(session, job, options, async (r) => {
    const ok = auto ? await r.auto(file, pkg) : await r.selected(file, pkg, method);
    if (ok && method !== "28") await r.grantPostInstall(file);
    return ok;
  });
}

export function startTool(session, id) {
  const title = Object.fromEntries(TOOLS)[id] || id;
  return runJob(session, new Job(title), {}, TOOL_FUNCS[id]);
}

export function startRemoveSystem(session, folder, pkg, options) {
  return runJob(session, new Job(`Удаление ${PRIV_APP_DIR}/${folder}`), options, (r) => r.removeSystem(folder, pkg));
}

export function lastReport() {
  return localStorage.getItem("dfm.lastReport") || "";
}
